import React, { useState } from 'react'

import { useActionless, useAppSelector } from 'redux/hooks'
import { useCanEditDeck } from 'redux/deck/selectors'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { SET_DECK_STATE } from 'redux/deck/actions/types'
import { SET_CACHEABLES_STATE } from 'redux/cacheables/actions/types'

import { NextPrevOptions } from 'components/elements/Overlay/CardDetailsOverlay'

import LineLoader from 'components/elements/LineLoader'
import ImageCard from 'components/card/deckCards/imageCard'
import CardModalStandalone from 'components/card/CardModal'
import CardEditionsGridOverlay from 'components/card/CardEditionsGridOverlay'
import { StandaloneCardGridOverlay } from 'components/deckPage/modals/cardComparisonOverlay'

import { CardType, defaultCard, SaveStatus } from 'types/deck'
import { ImageSize } from 'redux/cacheables/initialState'

import styles from './cardResultsGrid.module.scss'

export const gridClass = styles.container

type Props = {
  cards: CardType[]
  className?: string

  loading?: boolean

  nextPrevControls?: {
    previous: NextPrevOptions
    next: NextPrevOptions
  }

  onCardUpdated?: (cardIndex: number, updatedCard: CardType) => Promise<any>
}

const GridSizeClassNames: Record<ImageSize, string> = {
  '1': styles.small,
  '2': styles.normal,
  '3': styles.large,
}

const CardResultsGrid = ({ cards, onCardUpdated, className = '', loading }: Props) => {
  const canEditDeck = useCanEditDeck()

  const [saving, setSaving] = useState<SaveStatus>('saved')

  const [setActiveState, setDeckState] = useActionless(SET_ACTIVE_STATE, SET_DECK_STATE)

  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null)
  const [relatedCardIds, setRelatedCardIds] = useState(new Array<string>())
  const [openToEditionsGrid, setOpenToEditionsGrid] = useState(false)

  const searchImageSize = useAppSelector(state => state.cacheables.searchImageSize)

  const handleOpenCardDetails = (openCardToEditions: boolean, cardIndex: number) => {
    setActiveCardIndex(cardIndex)

    if (openCardToEditions) setOpenToEditionsGrid(true)
  }

  const handleClearDetails = () => {
    setActiveCardIndex(null)
    setOpenToEditionsGrid(false)
  }

  const handleUpdateCardIndex = (index: number, updatedCard: CardType) => {
    if (!onCardUpdated) return Promise.resolve()

    return onCardUpdated(index, updatedCard)
  }

  const handleUpdateCard = (updatedCard: CardType) => {
    if (activeCardIndex === null || !onCardUpdated) return

    setSaving('saving')

    return onCardUpdated(activeCardIndex, updatedCard)
      .then(() => setSaving('saved'))
      .catch(() => setSaving('error'))
  }

  const handleUpdateEdition = (updatedCard: CardType, skipOnGoBack?: boolean) => {
    return handleUpdateCard(updatedCard)?.then(() => {
      if (!skipOnGoBack) handleClearDetails()
    })
  }

  let prevCard: CardType | undefined = undefined
  let nextCard: CardType | undefined = undefined
  let nextPrevControls:
    | {
        previous: NextPrevOptions
        next: NextPrevOptions
      }
    | undefined = undefined

  if (activeCardIndex !== null) {
    const nextIndex = activeCardIndex === 0 ? cards.length - 1 : activeCardIndex - 1
    const prevIndex = activeCardIndex !== cards.length - 1 ? activeCardIndex + 1 : 0

    prevCard = cards[nextIndex]
    nextCard = cards[prevIndex]

    nextPrevControls = {
      previous: {
        label: prevCard?.name,
        onClick: () => setActiveCardIndex(nextIndex),
        disabled: !prevCard,
      },
      next: {
        label: nextCard?.name,
        onClick: () => setActiveCardIndex(prevIndex),
        disabled: !nextCard,
      },
    }
  }

  const gridClassName = GridSizeClassNames[searchImageSize]

  return (
    <div className={styles.queryableContainer}>
      {loading && <LineLoader fixed />}

      <div className={`${styles.container} ${gridClassName} ${className}`}>
        {cards.map((card, index) => (
          <ImageCard
            disableFaded
            disableDragging={!canEditDeck}
            noMultiSelect
            standaloneCard={card}
            onCardUpdated={updatedCard => handleUpdateCardIndex(index, updatedCard)}
            className={styles.imageDeckCard}
            wrapperClassName={styles.inDeckCard}
            basicCardClass={styles.imageDeckCard}
            imageClassName={styles.basicCard}
            key={card.id}
            cardId={card.id}
            actionsOverrides={{
              onCardClick: () => handleOpenCardDetails(false, index),
              openCardModal: openToEditionsGrid => handleOpenCardDetails(openToEditionsGrid, index),
              onDragStart: () => setActiveState({ hideStackOverlay: true }),
              onDragEnd: () => setActiveState({ hideStackOverlay: false }),
              onViewRelatedCards: () =>
                card.relatedCardIds.length === 1
                  ? setDeckState({ menuCardId: card.relatedCardIds[0] })
                  : setRelatedCardIds(card.relatedCardIds),
            }}
          />
        ))}
      </div>

      <CardModalStandalone
        open={activeCardIndex !== null && !openToEditionsGrid}
        card={activeCardIndex !== null ? cards[activeCardIndex] : defaultCard}
        onClose={handleClearDetails}
        onCardUpdated={handleUpdateCard}
        nextPrevControls={nextPrevControls}
        saving={saving}
      />

      <CardEditionsGridOverlay
        open={activeCardIndex !== null && openToEditionsGrid}
        card={activeCardIndex !== null ? cards[activeCardIndex] : defaultCard}
        onClose={handleClearDetails}
        onCardSelected={handleUpdateEdition}
        nextPrevControls={nextPrevControls}
      />

      <StandaloneCardGridOverlay
        header="In deck matches"
        open={!!relatedCardIds.length}
        onClose={() => setRelatedCardIds([])}
        cardIds={relatedCardIds}
      />
    </div>
  )
}

export default CardResultsGrid

type InDeckCardGridProps = {
  cardIds: string[]
  className?: string
}

export const InDeckCardGrid = ({ cardIds, className = '' }: InDeckCardGridProps) => (
  <div className={styles.queryableContainer}>
    <div className={`${styles.container} ${className}`}>
      {cardIds.map(cardId => (
        <ImageCard
          key={cardId}
          cardId={cardId}
          className={styles.imageDeckCard}
          wrapperClassName={styles.inDeckCard}
          basicCardClass={styles.imageDeckCard}
          imageClassName={styles.basicCard}
          hideMultiselectedHighlight
          disableActions
          disableFaded
        />
      ))}
    </div>
  </div>
)
